import { useLogger } from '../../composables/useLogger'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { openDB } from 'idb';
import type { IDBPDatabase, IDBPTransaction } from 'idb';
import type { ImageUpload } from './ImageUpload';

const DB_NAME = 'ImageStore';
const DB_VERSION = 2; // Increment this when you make changes to the database structure

const logger = useLogger()

async function openImageDB(): Promise<IDBPDatabase> {
    return openDB(DB_NAME, DB_VERSION, {
        upgrade(db: IDBPDatabase, oldVersion: number, newVersion: number | null, transaction: IDBPTransaction<unknown, string[], "versionchange">) {
            logger.debug(`Upgrading IndexedDB from version ${oldVersion} to ${newVersion}`);

            if (oldVersion < 1) {
                db.createObjectStore('images');
                logger.debug('Created "images" object store');
            }
            if (oldVersion < 2) {
                // Example of adding a new object store in version 2
                db.createObjectStore('metadata');
                logger.debug('Created "metadata" object store');
            }
            // Add more version upgrades here as needed
        },
    });
}

export class ImageBlob {
    private image_id: string;
    public blobUrl: string | null;
    private blob: Blob | null;
    public isLoaded: boolean;

    private constructor(image_id: string) {
        this.image_id = image_id;
        this.blobUrl = null;
        this.blob = null;
        this.isLoaded = false;
    }

    static async create(image_id: string): Promise<ImageBlob> {
        const instance = new ImageBlob(image_id);
        await instance.initialize();
        return instance;
    }

    private async initialize(): Promise<void> {
        // Check IndexedDB
        const storedBlob = await this.getFromIndexedDB();
        if (storedBlob) {
            logger.debug('ImageBlob: Using stored image from local storage');
            this.blob = storedBlob;
            this.blobUrl = URL.createObjectURL(storedBlob);
            this.isLoaded = true;
        }
    }

    // static method to get the supabase client
    static getSupabase() {
        return useSupabaseClientStore().supabase;
    }

    public static async createFromUpload(imageUpload: ImageUpload, image_id: string): Promise<ImageBlob> {
        const { data, error } = await ImageBlob.getSupabase()
            .storage
            .from('imageblobs')
            .upload(image_id, imageUpload.blob)
        if (error) {
            logger.error('101.5 Error uploading image to Supabase storage:', { error: error })
            logger.throwSupabaseError(error, `Failed to upload image blob ${image_id} to storage`)
        }
        logger.debug('101.5 New image uploaded:', { data: data })

        const imageBlob = new ImageBlob(image_id);
        imageBlob.blob = imageUpload.blob;
        imageBlob.blobUrl = URL.createObjectURL(imageUpload.blob);
        imageBlob.isLoaded = true;

        await imageBlob.saveToIndexedDB();

        return imageBlob;
    }

    async saveToIndexedDB(): Promise<void> {
        if (!this.blob) {
            logger.error('ImageBlob: No blob to save');
            return;
        }

        try {
            const db = await openImageDB();
            await db.put('images', this.blob, this.image_id);
            await db.put('metadata', { lastUpdated: new Date() }, this.image_id);
            logger.debug('ImageBlob: Saved to IndexedDB successfully');
        } catch (error) {
            logger.logErrorCaught('Error saving to IndexedDB', 'ImageBlob.ts', error);
            throw error;
        }
    }

    async getFromIndexedDB(): Promise<Blob | null> {
        try {
            const db = await openImageDB();
            const blob = await db.get('images', this.image_id);
            if (blob) {
                logger.debug('ImageBlob: Retrieved from IndexedDB', {
                    blobType: blob.type,
                    blobSize: blob.size
                });

                // Example of retrieving metadata
                const metadata = await db.get('metadata', this.image_id);
                logger.debug('ImageBlob: Retrieved metadata', metadata);

                return blob;
            }
        } catch (error) {
            logger.logErrorCaught('Error retrieving from IndexedDB', 'ImageBlob.ts', error);
            throw error;
        }
        return null;
    }

    /**
     * Gets the blob URL for the image.
     * If the blob URL is not already loaded, it will be fetched from Supabase and saved to IndexedDB.
     * If the blob URL is not in IndexedDB, it will be fetched from Supabase and saved to IndexedDB.
     * @returns A Promise that resolves to the blob URL.
     * @throws Error if the blob URL cannot be retrieved.
     */
    async getBlobUrl(): Promise<string> {
        if (this.isLoaded) return this.blobUrl!;

        // Try to get from IndexedDB
        const storedBlob = await this.getFromIndexedDB();
        if (storedBlob) {
            this.blob = storedBlob;
            this.blobUrl = URL.createObjectURL(storedBlob);
            this.isLoaded = true;
            return this.blobUrl;
        }

        // If not in IndexedDB, fetch from Supabase
        const { data, error } = await ImageBlob.getSupabase()
            .storage
            .from('imageblobs')
            .download(this.image_id);

        if (error) logger.throwSupabaseError(error, `Failed to download image blob ${this.image_id} from storage`);

        this.blob = data;
        this.blobUrl = URL.createObjectURL(this.blob);
        this.isLoaded = true;

        // Save to IndexedDB
        this.saveToIndexedDB();

        return this.blobUrl;
    }

    /**
     * Gets the blob for the image.
     * If the blob is not already loaded, it will be fetched from Supabase.
     * @returns A Promise that resolves to the blob.
     * @throws Error if the blob cannot be retrieved.
     */
    async getBlob(): Promise<Blob> {
        if (!this.isLoaded) {
            await this.getBlobUrl();
        }
        return this.blob!;
    }


    async rotateAndSave(direction: 'left' | 'right'): Promise<void> {
        if (!this.isLoaded) {
            await this.getBlobUrl();
        }

        // Use the static rotateBlob method
        const rotatedBlob = await ImageBlob.rotateBlob(this.blob!, direction);

        // Update Supabase storage
        const { error } = await ImageBlob.getSupabase()
            .storage
            .from('imageblobs')
            .update(this.image_id, rotatedBlob, {
                cacheControl: '3600',
                upsert: true
            });

        if (error) logger.throwSupabaseError(error, `Failed to update rotated image blob ${this.image_id} in storage`);

        // Update local properties
        this.blob = rotatedBlob;
        if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = URL.createObjectURL(rotatedBlob);

        // Update IndexedDB
        await this.saveToIndexedDB();

        logger.debug('ImageBlob: Rotated and saved successfully');
    }

    static async rotateBlob(blob: Blob, direction: 'left' | 'right'): Promise<Blob> {
        try {
            // Create a HTML image element
            const img = new Image();
            img.src = URL.createObjectURL(blob);

            await new Promise((resolve) => {
                img.onload = resolve;
            });

            // Create a canvas and rotate the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.height;
            canvas.height = img.width;

            ctx.translate(canvas.width / 2, canvas.height / 2);
            logger.debug('ImageBlob: Rotating image', { direction: direction })
            ctx.rotate(direction === 'left' ? Math.PI / 2 : -Math.PI / 2);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);

            // Convert canvas to blob
            const rotatedBlob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => resolve(blob!), blob!.type);
            });

            return rotatedBlob
        } catch (error) {
            logger.logErrorCaught('Error rotating image', 'ImageBlob.ts', error);
            throw error;
        }
    }

    release(): void {
        // This method is now empty as we've removed the cache
    }
}
